import { useCallback, useEffect, useState } from 'react';

/**
 * Состояние медиа-элемента: прогресс, буферизация, ошибки, play/pause.
 *
 * Стейт — единственный источник истины по громкости/мьюту: значения элемента
 * обратно НЕ принимаем. Если браузер сбросил громкость (переподключение к
 * ресурсу, особенности Safari), volumechange придёт со значением 1, и слепое
 * копирование в стейт уводило ползунок на 100% при play/pause. Вместо этого
 * возвращаем на элемент сохранённое значение. Во время drag стейт пишет
 * handleVolumeChange в родителе — там реассёрт не нужен.
 */
export const useMediaState = (
	videoRef,
	{ onReady, onError, onPlay, onPause, onEnded, isInteractingRef, volumeStateRef, mutedStateRef },
) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [buffered, setBuffered] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);

	// Возвращаем на элемент сохранённую громкость/мьют (например, после play/pause,
	// когда браузер мог пересоздать медиапайплайн и вернуть громкость к дефолту).
	const reassertVolume = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (video.volume !== volumeStateRef.current) {
			video.volume = volumeStateRef.current;
		}
		if (video.muted !== mutedStateRef.current) {
			video.muted = mutedStateRef.current;
		}
	}, [videoRef, volumeStateRef, mutedStateRef]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		if (video.readyState >= 1) {
			setDuration(video.duration);
			setCurrentTime(video.currentTime);
		}
		if (video.readyState >= 2) {
			setIsLoading(false);
		}

		const onLoadedMetadata = () => {
			setDuration(video.duration);
			setCurrentTime(video.currentTime);
		};

		const onTimeUpdate = () => {
			setCurrentTime(video.currentTime);
			if (video.buffered.length > 0) {
				setBuffered(video.buffered.end(video.buffered.length - 1));
			}
		};

		const onWaiting = () => setIsLoading(true);
		const onPlaying = () => setIsLoading(false);
		const onCanPlay = () => setIsLoading(false);
		const onPlayEvent = () => {
			setIsPlaying(true);
			setIsLoading(false);
			// браузер мог пересоздать медиапайплайн (переподключение к ресурсу) —
			// возвращаем на элемент сохранённую громкость, чтобы она не «отскакивала»
			reassertVolume();
			onPlay?.();
		};
		const onPauseEvent = () => {
			setIsPlaying(false);
			reassertVolume();
			onPause?.();
		};
		const onEndedEvent = () => {
			setIsPlaying(false);
			onEnded?.();
		};
		const onVolumeChange = () => {
			if (isInteractingRef.current) return;
			if (video.volume !== volumeStateRef.current) {
				video.volume = volumeStateRef.current;
			}
			if (video.muted !== mutedStateRef.current) {
				video.muted = mutedStateRef.current;
			}
		};
		const onProgress = () => {
			if (video.buffered.length > 0) {
				setBuffered(video.buffered.end(video.buffered.length - 1));
			}
		};
		const onErrorEvent = () => {
			setIsError(true);
			setIsLoading(false);
			onError?.();
		};
		const onLoadedData = () => {
			setIsLoading(false);
			onReady?.();
		};

		video.addEventListener('loadedmetadata', onLoadedMetadata);
		video.addEventListener('timeupdate', onTimeUpdate);
		video.addEventListener('waiting', onWaiting);
		video.addEventListener('playing', onPlaying);
		video.addEventListener('canplay', onCanPlay);
		video.addEventListener('play', onPlayEvent);
		video.addEventListener('pause', onPauseEvent);
		video.addEventListener('ended', onEndedEvent);
		video.addEventListener('volumechange', onVolumeChange);
		video.addEventListener('progress', onProgress);
		video.addEventListener('error', onErrorEvent);
		video.addEventListener('loadeddata', onLoadedData);

		return () => {
			video.removeEventListener('loadedmetadata', onLoadedMetadata);
			video.removeEventListener('timeupdate', onTimeUpdate);
			video.removeEventListener('waiting', onWaiting);
			video.removeEventListener('playing', onPlaying);
			video.removeEventListener('canplay', onCanPlay);
			video.removeEventListener('play', onPlayEvent);
			video.removeEventListener('pause', onPauseEvent);
			video.removeEventListener('ended', onEndedEvent);
			video.removeEventListener('volumechange', onVolumeChange);
			video.removeEventListener('progress', onProgress);
			video.removeEventListener('error', onErrorEvent);
			video.removeEventListener('loadeddata', onLoadedData);
		};
	}, [videoRef, onPlay, onPause, onEnded, onError, onReady, reassertVolume, isInteractingRef, volumeStateRef, mutedStateRef]);

	return { isPlaying, currentTime, duration, buffered, isLoading, isError, reassertVolume };
};
